/* @refresh reload */
// Client-side entrypoint. Mounts <App/> into #root with a TanStack Query
// client + Solid Router. Server-side rendering is intentionally not used —
// the dashboard is behind auth, so SEO has no value (see docs/idea/dashboard.md).
import { type QueryClient, QueryClientProvider } from '@tanstack/solid-query';
import { render } from 'solid-js/web';
import { App } from './App';
import { installClientErrorReporting, useClientErrorSession } from './lib/client-errors';
import { registerServiceWorker } from './lib/push';
import { createQueryClient } from './lib/query';
import './styles/main.scss';

const root = document.getElementById('root');
if (!root) throw new Error('#root not found in index.html');

const client: QueryClient = createQueryClient();

// Before the first render, so a crash while mounting reaches the console too. Render
// crashes the boundary catches report from `<AppErrorBoundary/>`; these are the ones
// nothing caught. A report leaves the browser only for a logged-in session, which
// <ClientErrorSession/> binds once the query provider exists (lib/client-errors.ts).
installClientErrorReporting(window);

function ClientErrorSession(): null {
  useClientErrorSession();
  return null;
}

render(
  () => (
    <QueryClientProvider client={client}>
      <ClientErrorSession />
      <App />
    </QueryClientProvider>
  ),
  root,
);

// PWA: register the service worker (offline shell + Web Push handler). Prod build
// only — in `vite dev` a SW would cache the dev bundle and fight HMR. Best-effort:
// a failed registration just means no offline shell / push, never a blocked app.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void registerServiceWorker().catch(() => {});
  });
}
